import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "./form";
import { InputHTMLAttributes, ReactNode } from "react";
import { Control } from "react-hook-form";
import { cn } from "@drpcrd/utils/shared";

type Props = {
  label?: ReactNode;
  description?: ReactNode;
  name: string;
  control: Control<any>;
  hideLabel?: boolean;
  inputLabel?: string;
  inputClassName?: string;
  labelClassName?: string;
  prefix?: string;
  prefixClassName?: string;
} & InputHTMLAttributes<HTMLInputElement>;

/**
 * InputTextField component for rendering a styled input field with label, description, and error handling.
 */
export const InputTextField = ({
  control,
  name,
  label,
  description,
  placeholder,
  hidden,
  hideLabel = false,
  onChange,
  className = "",
  inputClassName = "",
  labelClassName = "",
  prefix,
  prefixClassName = "",
  ...rest
}: Props) => (
  <FormField
    control={control}
    name={name}
    render={({ field, fieldState: { error } }) => (
      <FormItem className={cn(hidden ? "hidden" : "space-y-1.5", className)}>
        {(label || !hideLabel || description) && (
          <div>
            {label && !hideLabel && (
              <FormLabel
                className={cn(
                  "flex w-full flex-row gap-1 font-sans text-sm font-semibold",
                  labelClassName,
                )}
              >
                {label}
              </FormLabel>
            )}

            {description && (
              <FormDescription className="mt-0! mb-1!">
                {description}
              </FormDescription>
            )}
          </div>
        )}

        <FormControl className="w-full">
          <div
            className={cn(
              "flex w-full items-center overflow-hidden rounded-lg border bg-white",
              error
                ? "border-pink-500 focus-within:border-pink-500"
                : "border-neutral-500 focus-within:outline focus-within:outline-2 focus-within:outline-black",
            )}
          >
            {prefix && (
              <span
                className={cn(
                  "flex shrink-0 items-center border-r border-neutral-500 pr-1.5 pl-2 text-lg font-normal text-black",
                  prefixClassName,
                )}
              >
                {prefix}
              </span>
            )}
            <input
              {...rest}
              placeholder={placeholder}
              className={cn(
                "autofill-white mt-0! w-full flex-1 border-0 bg-transparent p-1.5 font-sans text-lg leading-2.75 font-normal outline-none placeholder:text-neutral-900",
                inputClassName,
              )}
              {...field}
              onChange={(event) => {
                if (onChange) {
                  onChange(event);
                } else if (rest.type === "number") {
                  field.onChange(+event.target.value);
                } else {
                  field.onChange(event);
                }
              }}
            />
          </div>
        </FormControl>

        {error?.message && (
          <p className="font-normal text-pink-500">🚨 {error?.message}</p>
        )}
      </FormItem>
    )}
  />
);
